import type { ShopperPageData } from "~/server/shopper";

/**
 * <head> for the hosted chat: the card WhatsApp, X or iMessage shows when the
 * link is pasted, the tab and home-screen icon (the retailer's logo; the root
 * leaves Hephon's out on this route), and the browser chrome in the
 * retailer's colour. Crawlable so previews work, noindex so the retailer's
 * own site stays the search result for its name.
 */
export function shopperHead(
	page: ShopperPageData | undefined,
	{
		title,
		description,
		path,
	}: { title: string; description: string; path: string },
) {
	const viewport = {
		name: "viewport",
		// Edge-to-edge in WebViews (safe-area insets apply) and let the
		// keyboard resize the page so the composer stays above it.
		content:
			"width=device-width, initial-scale=1, viewport-fit=cover, interactive-widget=resizes-content",
	};
	if (!page) {
		return {
			meta: [{ title: "Shopping assistant" }, viewport],
		};
	}
	const url = `${page.siteUrl}${path}`;
	return {
		meta: [
			{ title },
			{ name: "description", content: description },
			{ name: "robots", content: "noindex" },
			{ name: "theme-color", content: page.appearance.primaryColor },
			{ property: "og:type", content: "website" },
			{ property: "og:site_name", content: page.retailerName },
			{ property: "og:title", content: title },
			{ property: "og:description", content: description },
			{ property: "og:url", content: url },
			// Without a logo the root's Hephon card stays.
			...(page.shareCard
				? [
						{ property: "og:image", content: page.shareCard },
						{ property: "og:image:type", content: "image/png" },
						{ property: "og:image:width", content: "1200" },
						{ property: "og:image:height", content: "630" },
						{
							property: "og:image:alt",
							content: `${page.retailerName} logo`,
						},
						{
							name: "twitter:image:alt",
							content: `${page.retailerName} logo`,
						},
					]
				: []),
			{ name: "application-name", content: page.assistantName },
			{ name: "apple-mobile-web-app-title", content: page.assistantName },
			viewport,
		],
		links: [
			...(page.logoUrl
				? [
						{ rel: "icon", href: page.logoUrl },
						{ rel: "apple-touch-icon", href: page.logoUrl },
					]
				: [{ rel: "icon", href: "/favicon.ico", sizes: "32x32" }]),
		],
	};
}
